import { UntypedFormControl, ValidationErrors } from '@angular/forms';

export class PolishValidator {
  /**
   * Validation of identity card.
   * @param {*} value
   * @returns {ValidationErrors} null if is valid, ValidationErrors if invalid
   */
  public static identityCard({ value }: UntypedFormControl | { value: string }): ValidationErrors | null {
    // Check length
    if (!value || value.length !== 9) {
      return {
        identityCard: true,
      };
    }

    const upperNum = value.toUpperCase();
    const letterValues = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z',
    ];

    const getLetterValue = (letter: string) => {
      for (let j = 0, max = letterValues.length; j < max; j++) {
        if (letter === letterValues[j]) {
          return j;
        }
      }
      return -1;
    };

    // check series
    for (let i = 0; i < 3; i++) {
      if (getLetterValue(upperNum[i]) < 10) {
        return {
          identityCard: true,
        };
      }
    }
    // check number
    for (let i = 3; i < 9; i++) {
      if (getLetterValue(upperNum[i]) < 0 || getLetterValue(upperNum[i]) > 9) {
        return {
          identityCard: true,
        };
      }
    }

    // checksum
    let sum =
      7 * getLetterValue(upperNum[0]) +
      3 * getLetterValue(upperNum[1]) +
      getLetterValue(upperNum[2]) +
      7 * getLetterValue(upperNum[4]) +
      3 * getLetterValue(upperNum[5]) +
      getLetterValue(upperNum[6]) +
      7 * getLetterValue(upperNum[7]) +
      3 * getLetterValue(upperNum[8]);

    sum %= 10;

    if (sum !== getLetterValue(upperNum[3])) {
      return {
        identityCard: true,
      };
    }

    return null;
  }

  /**
   * Validation of PESEL.
   * @param {string} value
   * @returns {ValidationErrors} null if is valid, ValidationErrors if invalid
   */
  public static pesel({ value }: UntypedFormControl | { value: string }): ValidationErrors | null {
    if (!/^\d{11}$/.test(value)) {
      return {
        pesel: true,
      };
    }
    const times = [1, 3, 7, 9];
    const digits = `${value}`.split('').map((digit) => parseInt(digit, 10));
    const dig11 = digits.splice(-1)[0];
    const control =
      digits.reduce((previousValue, currentValue, index) => previousValue + currentValue * times[index % 4]) % 10;

    if (10 - (control === 0 ? 10 : control) !== dig11) {
      return {
        pesel: true,
      };
    }

    return null;
  }

  /**
   * Validation of NIP.
   * @param {*} value
   * @returns {ValidationErrors} null if is valid, ValidationErrors if invalid
   */
  public static nip({ value }: UntypedFormControl | { value: string }): ValidationErrors | null {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    if (value) {
      value = `${value}`;
    }
    if (typeof value !== 'string') {
      return {
        nip: true,
      };
    }

    const nipWithoutDashes = value.replace(/-/g, '');
    const reg = /^[0-9]{10}$/;
    if (reg.test(nipWithoutDashes) === false) {
      return {
        nip: true,
      };
    }

    const dig = ('' + nipWithoutDashes).split('');
    const control =
      (6 * parseInt(dig[0], 10) +
        5 * parseInt(dig[1], 10) +
        7 * parseInt(dig[2], 10) +
        2 * parseInt(dig[3], 10) +
        3 * parseInt(dig[4], 10) +
        4 * parseInt(dig[5], 10) +
        5 * parseInt(dig[6], 10) +
        6 * parseInt(dig[7], 10) +
        7 * parseInt(dig[8], 10)) %
      11;

    if (parseInt(dig[9], 10) !== control) {
      return {
        nip: true,
      };
    }

    return null;
  }

  /**
   * Validation of REGON.
   * @param {string} value
   * @returns {ValidationErrors} null if is valid, ValidationResult if invalid
   */
  public static regon({ value }: UntypedFormControl | { value: string }): ValidationErrors | null {
    const reg = /^\d{9,14}$/;
    if (reg.test(value) === false) {
      return {
        regon: true,
      };
    }

    const weights9: number[] = [8, 9, 2, 3, 4, 5, 6, 7];
    if (value.length === 9) {
      if (!PolishValidator._checksum(value, weights9)) {
        return {
          regon: true,
        };
      }
      return null;
    }

    const weights14: number[] = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8];
    if (!PolishValidator._checksum(value.slice(0, 9), weights9) || !PolishValidator._checksum(value, weights14)) {
      return {
        regon: true,
      };
    }
    return null;
  }

  /**
   * Checks if given number of identity card is valid.
   * @param {string} value - series and number of identity card to validate
   * @return {ValidationErrors} - null if identity card is valid, ValidationResult if invalid
   */
  public static identityCardWithSeparator({ value }: UntypedFormControl | { value: string }): ValidationErrors | null {
    // check length
    if (!value || value.length !== 10) {
      return {
        identityCardWithSeparator: true,
      };
    }

    // check separator
    if (value[3] !== ' ' && value[3] !== '-') {
      return {
        identityCardWithSeparator: true,
      };
    }

    if (!PolishValidator.identityCard({ value: value.replace(/[\s-]/g, '') })) {
      return {
        identityCardWithSeparator: true,
      };
    }

    return null;
  }

  /**
   * https://romek.info/ut/js-pesel.html
   * https://romek.info/ut/NrWykZaw.html#Farma
   * @param value
   */
  public static pwzfPharmacist({ value }: UntypedFormControl | { value: string }): ValidationErrors | null {
    const weights: number[] = [3, 1, 3, 1, 3, 1, 3, 0];
    if (value?.length !== 8) {
      return {
        npwzPharmacist: true,
      };
    }
    const sum = value
      .slice(0, value.length - 1)
      .split('')
      .reduce((acc: number, curr: string, index: number) => acc + weights[index] * parseInt(curr, 10), 0);
    const digit = (10 - (sum % 10)) % 10;
    const result = digit === parseInt(value[value.length - 1], 10);
    if (!result) {
      return {
        npwzPharmacist: true,
      };
    }
    const code = parseInt(value.slice(0, 2), 10);
    if (code < 1 || code > 20) {
      return {
        npwzPharmacist: true,
      };
    }
    return null;
  }

  private static _checksum(number: string, weights: number[]): boolean {
    const max = number.length - 1;
    let sum = 0;

    for (let i = 0; i < max; i++) {
      const n = parseInt(number[i], 10);
      sum += n * weights[i];
    }

    const resultSum = sum % 11 !== 10 ? sum % 11 : 0;
    const lastDigit = parseInt(number.slice(-1), 10);
    return resultSum === lastDigit;
  }
}
